import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { FlagProvider, useFlagsStatus, useUnleashClient } from '@unleash/proxy-client-react';
import config from '../config';
import { useAuthContext } from '../contexts/AuthContext';
import { PLATFORM_ADMIN_ROLE, isPlatformAdminRole } from '../lib/platformRoles';

// ─── Context ──────────────────────────────────────────────────────────────────

export type FeatureFlagSource = 'bypass' | 'unleash' | 'unleash-failed';

interface FeatureFlagContextValue {
  source: FeatureFlagSource;
  ready: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  source: 'unleash',
  ready: false,
});

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);

// ─── Context Sync (inside FlagProvider) ──────────────────────────────────────

/**
 * Rendered inside <FlagProvider> so it can call useUnleashClient().
 * Syncs the authenticated AMS user context into Unleash so that
 * account-scoped targeting strategies work.
 * Skipped in bypass mode (no-op when source='bypass').
 *
 * Two role dimensions travel here, and they are not interchangeable (task 684):
 * `role` is the User's role in the *selected account* and flips on every
 * account switch, while `platformRole` is the platform-scope grant that does
 * not. Cohort targeting needs both, so both are sent.
 *
 * Absent, never empty (task 713): every property whose value is unknown is
 * OMITTED from the context rather than sent as `''`. An Unleash `IN` constraint
 * on an empty string is still a value a badly written segment could match,
 * whereas an absent property cannot match at all. This applies uniformly to
 * `platformRole` (omitted for non-staff Users), `role` and `accountId` (omitted
 * while no account scope has resolved) — the same rule the two backends and the
 * admin app follow, so one canonical context shape reaches Unleash from all
 * four consumers.
 *
 * `user` is rebuilt on every token refresh and re-auth, so this re-runs on
 * account switch and the new token's role lands in the context without any
 * extra machinery.
 */
function UnleashContextSync({
  onUserContextReady,
}: {
  onUserContextReady: (ready: boolean) => void;
}) {
  const { user, isLoading: authLoading } = useAuthContext();
  const client = useUnleashClient();
  const isPlatformAdmin = isPlatformAdminRole(user?.platformRoles);

  // Depend on the identity VALUES, not the `user` object: AuthProvider rebuilds
  // that object on every token refresh, and re-pushing an identical context
  // would drop readiness (and so unmount flag-gated routes) every ~30s.
  const userId = user?.userId;
  const accountId = user?.accountId;
  const role = user?.accountRole;

  useEffect(() => {
    if (!client) return undefined;

    // While auth is still resolving there is no identity to evaluate against.
    // Report not-ready rather than pushing an anonymous context and letting a
    // consumer act on the answer — that is precisely the task-692 defect.
    if (authLoading) {
      onUserContextReady(false);
      return undefined;
    }

    let cancelled = false;
    // A new identity invalidates the previous answer until its fetch lands.
    onUserContextReady(false);

    void Promise.resolve(
      client.updateContext({
        ...(userId !== undefined ? { userId } : {}),
        properties: {
          ...(accountId !== undefined ? { accountId } : {}),
          ...(role !== undefined ? { role } : {}),
          ...(isPlatformAdmin ? { platformRole: PLATFORM_ADMIN_ROLE } : {}),
        },
      })
    ).then(() => {
      // `updateContext` resolves only after the client has re-fetched against
      // the new context, so this is the first moment a cohort-gated flag has a
      // truthful answer for this User.
      if (!cancelled) onUserContextReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [client, userId, accountId, role, isPlatformAdmin, authLoading, onUserContextReady]);

  return null;
}

// ─── Inner wrapper (inside FlagProvider) ─────────────────────────────────────

interface InnerProps {
  children: React.ReactNode;
  bypass: boolean;
  configMissing: boolean;
}

function UnleashInner({ children, bypass, configMissing }: InnerProps) {
  const { flagsReady, flagsError } = useFlagsStatus();
  /**
   * Has a fetch completed against a context carrying THIS User's identity?
   *
   * `flagsReady` cannot answer that. It turns true after the client's first
   * fetch, and that fetch races the auth handshake — captured live in task 705:
   *
   *   sync effect   authLoading=true  role=ABSENT     <- no identity yet
   *   FETCH         no role property                  <- context-less
   *   sync effect   authLoading=false role=owner      <- identity arrives
   *   flagsReady    true                              <- flips on the identity-less fetch
   *   FlaggedRoute  enabled=false -> REDIRECT         <- 2ms too early
   *   updateContext resolved for role=owner           <- the truthful answer
   *
   * Every cohort-gated flag evaluates false against a missing `role`, so the
   * guard redirected users who were squarely inside the cohort. Flags without
   * cohorts never noticed: their bare default strategy answers true either way.
   * (Task 705 captured this while the unknown role was sent as `''`; task 713
   * changed that to an omitted property, which the cohorts read identically —
   * neither value matches an `IN` constraint — so the race and its cure are
   * unchanged.)
   *
   * Note this is NOT "a context was applied" — task 692 tried that and it did
   * not cure the race, because a context *was* applied at the arrow above; it
   * simply carried no identity.
   */
  const [userContextReady, setUserContextReady] = useState(false);
  const markUserContextReady = useCallback((value: boolean) => {
    setUserContextReady(value);
  }, []);
  let source: FeatureFlagSource;
  let ready: boolean;

  if (bypass) {
    source = 'bypass';
    ready = true;
  } else if (configMissing || flagsError) {
    // Missing Unleash config lands in the same degraded mode as a failing
    // Unleash (task 349): the shell renders, useFlag() applies the outage
    // matrix. It must never crash the tree the way an undefined url handed
    // to the unleash client constructor does.
    source = 'unleash-failed';
    ready = true; // failed — stop waiting, fail closed
  } else {
    source = 'unleash';
    ready = flagsReady && userContextReady;
  }

  return (
    <FeatureFlagContext.Provider value={{ source, ready }}>
      {!bypass && !configMissing && <UnleashContextSync onUserContextReady={markUserContextReady} />}
      {children}
    </FeatureFlagContext.Provider>
  );
}

// ─── FeatureFlagProvider ──────────────────────────────────────────────────────

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/**
 * Wraps @unleash/proxy-client-react's <FlagProvider> with config from
 * src/config/index.ts. Always mounts <FlagProvider> (even in bypass mode)
 * so that useFlag() from the unleash library always has a valid context.
 * Exposes a FeatureFlagContext with { source, ready }.
 *
 * `ready` means "ready for this User" (task 705): flags have loaded AND a fetch
 * has completed against a context carrying the authenticated identity. Consumers
 * that navigate on a flag answer — FlaggedRoute, HomeRoute, RunnerRoute,
 * ComposerRedirect — all gate on it, so none can act on a pre-identity
 * evaluation. An unauthenticated render resolves as soon as auth reports it has
 * no user, so anonymous pages behave exactly as before.
 *
 * In bypass mode (FEATURE_FLAGS_BYPASS=true):
 * - FlagProvider is mounted with disableRefresh=true and no network calls
 * - source='bypass', ready=true
 * - useFlag() always returns true (overridden in our hook layer)
 *
 * On Unleash error:
 * - source='unleash-failed'; permission flags resolve false (fail closed)
 */
let missingConfigLogged = false;

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const bypass = config.FEATURE_FLAGS_BYPASS;

  // Missing Unleash config must degrade, not crash (task 349): the unleash
  // client constructor throws synchronously on an undefined/empty url, which
  // unmounts the entire tree — the prd blank-page incident. Detect it here,
  // mount the no-op offline config instead, and force unleash-failed mode.
  // No fallback URL is invented ("fail visibly"): the failure is a rendered
  // degraded mode plus a console.error naming the variable(s).
  const missingVars: string[] = [];
  if (!bypass && !config.UNLEASH_URL) missingVars.push('VITE_UNLEASH_URL');
  if (!bypass && !config.UNLEASH_CLIENT_KEY) missingVars.push('VITE_UNLEASH_CLIENT_KEY');
  const configMissing = missingVars.length > 0;
  if (configMissing && !missingConfigLogged) {
    missingConfigLogged = true;
    console.error(
      `[feature-flags] Missing required env: ${missingVars.join(', ')}. ` +
      'Feature flags are degraded to unleash-failed mode (permission flags OFF). ' +
      'Set the variable(s) and rebuild.'
    );
  }

  // Always mount FlagProvider so that hooks from @unleash/proxy-client-react
  // (useFlag, useFlagsStatus, useUnleashClient) always have a valid context.
  // In bypass mode use a no-op offline config so no network requests are made.
  const unleashConfig = bypass || configMissing
    ? {
        url: 'http://localhost',
        clientKey: bypass ? 'bypass' : 'missing-config',
        appName: config.UNLEASH_APP_NAME || 'innovate-app',
        disableRefresh: true,
        disableMetrics: true,
        bootstrap: [],
        bootstrapOverride: false,
      }
    : {
        url: config.UNLEASH_URL,
        clientKey: config.UNLEASH_CLIENT_KEY,
        appName: config.UNLEASH_APP_NAME,
        refreshInterval: 15,
      };

  return (
    <FlagProvider config={unleashConfig}>
      <UnleashInner bypass={bypass} configMissing={configMissing}>{children}</UnleashInner>
    </FlagProvider>
  );
}
